import React from 'react';
import { bool, object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { H1, H2, H3, Page, LayoutSingleColumn, NamedLink } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import {
  ALL_FILE_NAME,
  CATEGORIES,
  SHARED_PARAMETERS,
  SCHEDULE_COLUMNS,
  SETUP_STEPS,
  buildSharedParameterFile,
  forCategory,
} from './parameters';

import css from './RevitIntegrationPage.module.css';

const PLUGIN_DOWNLOAD_URL = '/static/downloads/LoopbuildRevitInstaller.msi';

// Generated in the browser rather than served from /static: one source of truth for
// the parameters, and every category gets its own file for free.
const download = (fileName, params) => {
  const url = URL.createObjectURL(
    new Blob([buildSharedParameterFile(params)], { type: 'text/plain' })
  );
  const link = Object.assign(document.createElement('a'), { href: url, download: fileName });
  // Firefox and Safari ignore .click() on an anchor that is not in the document, and
  // revoking the URL in the same tick can cancel the download that just started.
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const DownloadButton = props => {
  const { fileName, params, className, children } = props;
  return (
    <button
      type="button"
      className={className || css.downloadButton}
      onClick={() => download(fileName, params)}
    >
      {children}
    </button>
  );
};

const ParameterTable = ({ params }) => {
  const intl = useIntl();
  return (
    <div className={css.tableScroll}>
      <table className={css.table}>
        <thead>
          <tr>
            <th>
              <FormattedMessage id="RevitIntegrationPage.table.parameter" />
            </th>
            <th>
              <FormattedMessage id="RevitIntegrationPage.table.appliesTo" />
            </th>
            <th>
              <FormattedMessage id="RevitIntegrationPage.table.binding" />
            </th>
            <th>
              <FormattedMessage id="RevitIntegrationPage.table.example" />
            </th>
            <th>
              <FormattedMessage id="RevitIntegrationPage.table.purpose" />
            </th>
          </tr>
        </thead>
        <tbody>
          {params.map(p => (
            <tr key={p.name}>
              <td>
                <code className={css.code}>{p.name}</code>
                {p.required ? (
                  <span className={css.requiredTag}>
                    <FormattedMessage id="RevitIntegrationPage.table.required" />
                  </span>
                ) : null}
              </td>
              <td>
                {p.appliesTo
                  .map(c => intl.formatMessage({ id: `RevitIntegrationPage.category.${c}` }))
                  .join(', ')}
              </td>
              <td>
                <FormattedMessage id={`RevitIntegrationPage.binding.${p.binding}`} />
              </td>
              <td>
                <code className={css.code}>{p.example}</code>
              </td>
              <td>
                <FormattedMessage id={p.descriptionId} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ColumnTable = ({ columns }) => (
  <div className={css.tableScroll}>
    <table className={css.table}>
      <thead>
        <tr>
          <th>
            <FormattedMessage id="RevitIntegrationPage.table.column" />
          </th>
          <th>
            <FormattedMessage id="RevitIntegrationPage.table.purpose" />
          </th>
        </tr>
      </thead>
      <tbody>
        {columns.map(c => (
          <tr key={c.name}>
            <td>
              <code className={css.code}>{c.name}</code>
            </td>
            <td>
              <FormattedMessage id={c.descriptionId} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const CategoryCards = () => (
  <div className={css.cards}>
    {CATEGORIES.map(cat => (
      <NamedLink
        key={cat.id}
        className={css.card}
        name="RevitIntegrationPage"
        params={{ category: cat.id }}
      >
        <H3 className={css.cardTitle}>
          <FormattedMessage id={`RevitIntegrationPage.category.${cat.id}`} />
        </H3>
        <p className={css.cardText}>
          <FormattedMessage id={`RevitIntegrationPage.categoryLead.${cat.id}`} />
        </p>
        <span className={css.cardMeta}>
          <FormattedMessage
            id="RevitIntegrationPage.card.parameterCount"
            values={{ count: forCategory(SHARED_PARAMETERS, cat.id).length }}
          />
        </span>
      </NamedLink>
    ))}
  </div>
);

const IndexView = () => (
  <>
    <header className={css.hero}>
      <H1 className={css.title}>
        <FormattedMessage id="RevitIntegrationPage.title" />
      </H1>
      <p className={css.lead}>
        <FormattedMessage id="RevitIntegrationPage.lead" />
      </p>
      <div className={css.heroActions}>
        <DownloadButton fileName={ALL_FILE_NAME} params={SHARED_PARAMETERS}>
          <FormattedMessage id="RevitIntegrationPage.downloadAllButton" />
        </DownloadButton>
        <a className={css.downloadButton} href={PLUGIN_DOWNLOAD_URL}>
          <FormattedMessage id="RevitIntegrationPage.downloadPluginButton" />
        </a>
      </div>
      <p className={css.fileNote}>
        <FormattedMessage
          id="RevitIntegrationPage.downloadNote"
          values={{ count: SHARED_PARAMETERS.length, fileName: ALL_FILE_NAME }}
        />
      </p>
    </header>

    <section className={css.section}>
      <CategoryCards />
    </section>

    <section className={css.section}>
      <H2>
        <FormattedMessage id="RevitIntegrationPage.setup.title" />
      </H2>
      <ol className={css.steps}>
        {SETUP_STEPS.map(id => (
          <li key={id}>
            <FormattedMessage id={id} />
          </li>
        ))}
      </ol>
    </section>

    <section className={css.section}>
      <H2>
        <FormattedMessage id="RevitIntegrationPage.troubleshooting.title" />
      </H2>
      <dl className={css.faq}>
        <dt>
          <FormattedMessage id="RevitIntegrationPage.troubleshooting.noMatchQuestion" />
        </dt>
        <dd>
          <FormattedMessage id="RevitIntegrationPage.troubleshooting.noMatchAnswer" />
        </dd>
        <dt>
          <FormattedMessage id="RevitIntegrationPage.troubleshooting.mergedLinesQuestion" />
        </dt>
        <dd>
          <FormattedMessage id="RevitIntegrationPage.troubleshooting.mergedLinesAnswer" />
        </dd>
        <dt>
          <FormattedMessage id="RevitIntegrationPage.troubleshooting.frenchQuestion" />
        </dt>
        <dd>
          <FormattedMessage id="RevitIntegrationPage.troubleshooting.frenchAnswer" />
        </dd>
        <dt>
          <FormattedMessage id="RevitIntegrationPage.troubleshooting.unitsQuestion" />
        </dt>
        <dd>
          <FormattedMessage id="RevitIntegrationPage.troubleshooting.unitsAnswer" />
        </dd>
      </dl>
    </section>
  </>
);

const CategoryView = ({ category }) => {
  const params = forCategory(SHARED_PARAMETERS, category.id);
  const columns = forCategory(SCHEDULE_COLUMNS, category.id);

  return (
    <>
      <NamedLink className={css.backLink} name="RevitIntegrationPage">
        <FormattedMessage id="RevitIntegrationPage.back" />
      </NamedLink>

      <header className={css.hero}>
        <H1 className={css.title}>
          <FormattedMessage id={`RevitIntegrationPage.category.${category.id}`} />
        </H1>
        <p className={css.lead}>
          <FormattedMessage id={`RevitIntegrationPage.categoryLead.${category.id}`} />
        </p>
        <DownloadButton fileName={category.fileName} params={params}>
          <FormattedMessage id="RevitIntegrationPage.downloadCategoryButton" />
        </DownloadButton>
        <p className={css.fileNote}>
          <FormattedMessage
            id="RevitIntegrationPage.downloadNote"
            values={{ count: params.length, fileName: category.fileName }}
          />
        </p>
      </header>

      <section className={css.section}>
        <H2>
          <FormattedMessage id="RevitIntegrationPage.parameters.title" />
        </H2>
        <p className={css.sectionLead}>
          <FormattedMessage id="RevitIntegrationPage.parameters.lead" />
        </p>
        <ParameterTable params={params} />
      </section>

      <section className={css.section}>
        <H2>
          <FormattedMessage id="RevitIntegrationPage.columns.title" />
        </H2>
        <p className={css.sectionLead}>
          <FormattedMessage id="RevitIntegrationPage.columns.lead" />
        </p>
        <ColumnTable columns={columns} />
      </section>
    </>
  );
};

export const RevitIntegrationPageComponent = props => {
  const intl = useIntl();
  const { scrollingDisabled, params } = props;

  // An unknown /revit-integration/:category falls back to the index rather than 404:
  // the cards are exactly what someone with a stale link needs to see.
  const category = CATEGORIES.find(c => c.id === params?.category);
  const titleId = category
    ? `RevitIntegrationPage.category.${category.id}`
    : 'RevitIntegrationPage.schemaTitle';

  return (
    <Page
      title={intl.formatMessage({ id: titleId })}
      description={intl.formatMessage({ id: 'RevitIntegrationPage.schemaDescription' })}
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
        mainColumnClassName={css.layoutWrapperMain}
      >
        <article className={css.root}>
          {category ? <CategoryView category={category} /> : <IndexView />}
        </article>
      </LayoutSingleColumn>
    </Page>
  );
};

RevitIntegrationPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  params: object,
};

const mapStateToProps = state => ({
  scrollingDisabled: isScrollingDisabled(state),
});

const RevitIntegrationPage = compose(connect(mapStateToProps))(RevitIntegrationPageComponent);

export default RevitIntegrationPage;
